import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Search, FileText, MapPin, ArrowRight, Loader2 } from 'lucide-react'
import api from '../../services/api'
import { useDebounce } from '../../hooks/useDebounce'

/**
 * OfferteFinder — dashboard-widget om snel een offerte te vinden en te openen (1 klik).
 *
 * Eén slimme zoekbalk (matcht server-side over offertenummer, klantnaam, projectnaam en
 * woonplaats: klant-stad + projectlocatie + leveringsadres) + status-chips. Keyboard-first:
 * autofocus + Enter opent de bovenste treffer. Lege zoekterm = recentste offertes.
 * Bron: GET /offertes/zoek.
 */
const STATUS_CHIPS = [
  { value: '', label: 'Alle' },
  { value: 'concept', label: 'Concept' },
  { value: 'verzonden', label: 'Verzonden' },
  { value: 'geaccepteerd', label: 'Geaccepteerd' },
]

export default function OfferteFinder() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [q, setQ] = useState('')
  const [status, setStatus] = useState('')
  const debouncedQ = useDebounce(q, 250)

  const { data: resultaten = [], isFetching } = useQuery({
    queryKey: ['offerte-zoek', debouncedQ, status],
    queryFn: async () => (await api.get('/offertes/zoek', {
      params: { q: debouncedQ || undefined, status: status || undefined, limit: 25 },
    })).data || [],
    placeholderData: (prev) => prev,  // v5: behoud vorige resultaten tijdens refetch (geen flikkering)
    staleTime: 15000,
  })

  const openOfferte = (id) => navigate(`/offertes/${id}`)
  const onKeyDown = (e) => {
    if (e.key === 'Enter' && resultaten.length > 0) openOfferte(resultaten[0].id)
  }

  const statusBadge = (s) => {
    const k = (s || '').toLowerCase()
    const cls = k === 'geaccepteerd' ? 'badge-success' : (k === 'geweigerd' || k === 'afgewezen') ? 'badge-danger' : 'badge-warning'
    return <span className={`badge ${cls} shrink-0`}>{s || '—'}</span>
  }

  return (
    <div className="bg-surface dark:bg-gray-800 rounded-card border border-line dark:border-gray-700 border-l-4 border-l-blue-600 shadow-card p-5 mb-8">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div>
          <p className="text-xs font-semibold text-accent uppercase tracking-[0.2em]">{t('dashboard.offerteFinder.eyebrow', 'Snel openen')}</p>
          <h2 className="text-lg font-bold text-ink dark:text-white">{t('dashboard.offerteFinder.titel', 'Offerte zoeken')}</h2>
        </div>
        <button onClick={() => navigate('/offertes')} className="text-sm text-accent hover:underline inline-flex items-center gap-1 shrink-0">
          {t('dashboard.offerteFinder.alle', 'Alle offertes')} <ArrowRight className="w-4 h-4" />
        </button>
      </div>

      {/* Slimme zoekbalk */}
      <div className="relative mb-3">
        <Search className="w-5 h-5 text-muted absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={t('dashboard.offerteFinder.placeholder', 'Zoek op klant, woonplaats, projectnaam of offertenummer…')}
          className="w-full pl-10 pr-10 py-2.5 border border-line dark:border-gray-600 rounded-control bg-canvas dark:bg-gray-700 dark:text-white"
        />
        {isFetching && <Loader2 className="w-4 h-4 animate-spin text-muted absolute right-3 top-1/2 -translate-y-1/2" />}
      </div>

      {/* Status-chips */}
      <div className="flex flex-wrap gap-1.5 mb-2">
        {STATUS_CHIPS.map((c) => (
          <button
            key={c.value}
            onClick={() => setStatus(c.value)}
            className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${status === c.value ? 'bg-accent/10 text-accent border-accent/30' : 'border-line dark:border-gray-600 text-muted hover:bg-gray-50 dark:hover:bg-gray-700'}`}
          >
            {c.label}
          </button>
        ))}
      </div>

      {/* Resultaten */}
      <div className="divide-y divide-line dark:divide-gray-700 max-h-80 overflow-y-auto">
        {resultaten.length === 0 ? (
          <div className="py-6 text-sm text-muted text-center">
            {(debouncedQ || status)
              ? t('dashboard.offerteFinder.geen', 'Geen offertes gevonden.')
              : t('dashboard.offerteFinder.start', 'Begin te typen om te zoeken — of kies een status.')}
          </div>
        ) : resultaten.map((o) => (
          <button
            key={o.id}
            onClick={() => openOfferte(o.id)}
            className="w-full text-left px-2 py-2.5 flex items-center justify-between gap-3 hover:bg-gray-50 dark:hover:bg-gray-700/40 rounded-control"
          >
            <div className="min-w-0">
              <div className="text-sm font-medium text-ink dark:text-white truncate flex items-center gap-2">
                <FileText className="w-4 h-4 text-muted shrink-0" />
                <span className="truncate">{o.klant_naam || o.project_naam || t('dashboard.offerteFinder.offerte', 'Offerte')}</span>
                {o.offerte_nummer && <span className="font-mono text-xs text-muted shrink-0">{o.offerte_nummer}</span>}
              </div>
              <div className="text-xs text-muted flex flex-wrap gap-x-2 mt-0.5 pl-6">
                {o.plaats && <span className="inline-flex items-center gap-0.5"><MapPin className="w-3 h-3" /> {o.plaats}</span>}
                {o.project_naam && o.klant_naam && <span>· {o.project_naam}</span>}
                {o.datum && <span>· {new Date(o.datum).toLocaleDateString('nl-BE')}</span>}
                {o.bedrag != null && <span>· € {Number(o.bedrag).toFixed(2)}</span>}
              </div>
            </div>
            {statusBadge(o.status)}
          </button>
        ))}
      </div>
      {resultaten.length > 0 && (
        <p className="text-[11px] text-muted mt-2">
          {t('dashboard.offerteFinder.enterTip', 'Tip: druk Enter om de bovenste offerte te openen.')}
        </p>
      )}
    </div>
  )
}
